import { useCallback, useEffect, useMemo, useRef } from 'react';
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from 'react-query';

import { TalentPermissionsDTO, useGetTalentPermissions } from '@/api/generated';
import { useTalent } from '@/hooks/entities/useTalent';
import { getErrorStatus } from '@/utils/error';

import { PermissionContextVariables } from './usePermissions';

type UseTalentPermissionsResult = {
  value?: TalentPermissionsDTO;
  error: unknown;
  isLoading: boolean;
  isAccessDenied: boolean;
  refetch: <TPageData>(
    options?: RefetchOptions & RefetchQueryFilters<TPageData>
  ) => Promise<QueryObserverResult<TalentPermissionsDTO, unknown>> | undefined;
};

export const useTalentPermissions = ({
  variables
}: {
  variables?: PermissionContextVariables;
} = {}): UseTalentPermissionsResult => {
  const talent = useTalent();

  const talentId = variables?.talentId ?? talent.talentId;

  const {
    data: talentPermissions,
    error,
    isLoading,
    refetch
  } = useGetTalentPermissions(talentId!, {
    query: { enabled: !!talentId }
  });

  const isAccessDenied = useMemo(() => [401, 403, 404].includes(getErrorStatus(error) ?? 0), [error]);

  const canReturnValue = !!talentId && !isAccessDenied;

  const canRefetchValueRef = useRef(canReturnValue);

  useEffect(() => {
    canRefetchValueRef.current = canReturnValue;
  }, [canReturnValue]);

  const refetchWithCanReturnValue = useCallback(
    <TPageData>(options?: RefetchOptions & RefetchQueryFilters<TPageData>) => {
      if (canRefetchValueRef.current) {
        return refetch(options);
      }
    },
    [refetch]
  );

  return {
    value: canReturnValue ? talentPermissions || {} : undefined,
    error,
    isLoading,
    isAccessDenied,
    refetch: refetchWithCanReturnValue
  };
};
